import React, { Fragment, useMemo } from 'react';
import { useParams, Navigate } from 'react-router-dom';
import Link from 'shared/SlashLink';
import allProjects from '../../data/ca_housing_pipeline.json';
import usePageSeo from '../../hooks/usePageSeo';
import Header from '../../components/header/Header';
import PageTitle from '../../components/pagetitle/PageTitle';
import Scrollbar from '../../components/scrollbar/scrollbar';
import Footer from '../../components/footer/Footer';
import CtaSection from '../../components/CtaSection/CtaSection';
import PipelineExplorer from '../../components/PipelineExplorer/PipelineExplorer';
import PipelineCTA from '../../components/PipelineCTA/PipelineCTA';
import { doorFeeIncome } from '../../data/doorFee';
import { builderLink } from '../../data/builderSites';
import { isDeveloperIndexed } from '../../data/pipelineSeo';

const money = (n) => (n >= 1e6 ? '$' + (n / 1e6).toFixed(1) + 'M' : '$' + Math.round(n / 1e3) + 'K');

const HousingDeveloperPage = () => {
    const { devSlug } = useParams();

    const projects = useMemo(
        () => allProjects.filter((p) => p.developerSlug === devSlug),
        [devSlug]
    );
    const devName = projects[0]?.developer || '';

    const stats = useMemo(() => {
        let units = 0, nine = 0, four = 0, doorFeeTotal = 0;
        const counties = new Set();
        projects.forEach((p) => {
            units += p.totalUnits || 0;
            doorFeeTotal += doorFeeIncome(p.totalUnits);
            if (p.creditType === '9%') nine++; else if (p.creditType === '4%') four++;
            counties.add(p.county);
        });
        return { units, nine, four, doorFeeTotal, counties: counties.size };
    }, [projects]);

    usePageSeo({
        title: `${devName} — CA Affordable Housing`,
        description: `${devName} has ${projects.length} affordable housing projects in the California LIHTC pipeline — ${stats.units.toLocaleString()} units across ${stats.counties} ${stats.counties === 1 ? 'county' : 'counties'}, with builders, unit counts, and locations from CTCAC data.`,
        keywords: `${devName}, ${devName} affordable housing, ${devName} LIHTC projects, ${devName} developments California, affordable housing developer`,
        // Only substantial multi-project developers are indexed; thin single-project
        // SPE pages stay browsable but noindex (see src/data/pipelineSeo.js).
        noindex: !isDeveloperIndexed(devSlug),
    });

    if (!devName) return <Navigate to="/housing-pipeline" replace />;

    const site = builderLink(devName);

    const schema = {
        '@context': 'https://schema.org',
        '@type': 'ItemList',
        name: `${devName} — Affordable Housing Pipeline`,
        numberOfItems: projects.length,
        itemListElement: projects.slice(0, 50).map((p, i) => ({ '@type': 'ListItem', position: i + 1, name: p.name })),
    };

    const StatCard = ({ value, label }) => (
        <div className="col-6 col-lg mb-3">
            <div className="text-center p-3 h-100" style={{ background: '#fff', border: '1px solid #ead9cd', borderRadius: 14 }}>
                <div className="h2 mb-0" style={{ color: '#B85C38' }}>{value}</div>
                <div className="small text-muted">{label}</div>
            </div>
        </div>
    );

    return (
        <Fragment>
            <Header />
            <main id="main-content" className="page_content">
                <PageTitle pageTitle={devName} pagesub={'Developer'} pageTop={'Affordable Housing'} />
                <section className="section_space bg-light">
                    <div className="container">
                        <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }} />

                        <nav aria-label="breadcrumb" className="mb-3">
                            <Link to="/housing-pipeline">California Pipeline</Link>
                            <span className="text-muted"> / {devName}</span>
                        </nav>

                        <div className="row mb-4">
                            <div className="col-lg-9">
                                <h2 className="mb-3">{devName} — affordable housing pipeline</h2>
                                <p>
                                    {devName} has <strong>{projects.length} affordable housing project{projects.length === 1 ? '' : 's'}</strong> in
                                    the recent California LIHTC pipeline — {stats.nine} funded with 9% tax credits and {stats.four} with 4% credits,
                                    totaling roughly <strong>{stats.units.toLocaleString()} units</strong> across {stats.counties} {stats.counties === 1 ? 'county' : 'counties'}.
                                </p>
                                <p className="text-muted">
                                    Working with {devName} on an upcoming development? BUILDLAB designs the low-voltage, security, and
                                    life-safety scope these projects depend on — structured cabling, surveillance, door access, and
                                    fire &amp; life safety, engineered in-house.
                                </p>
                                {site && (
                                    <a href={site.url} target="_blank" rel="noopener noreferrer" className="btn btn-outline-dark" style={{ padding: '12px 26px', fontWeight: 600 }}>
                                        {site.direct ? `Visit ${devName}` : `Search for ${devName}`}
                                        <span className="btn_icon ms-2"><i className="fa-solid fa-arrow-up-right-from-square" aria-hidden="true"></i></span>
                                    </a>
                                )}
                            </div>
                        </div>

                        <div className="row mb-5">
                            <StatCard value={projects.length} label="Projects" />
                            <StatCard value={stats.units.toLocaleString()} label="Total units" />
                            <StatCard value={money(stats.doorFeeTotal)} label="Potential door-fee income" />
                            <StatCard value={`${stats.nine} / ${stats.four}`} label="9% / 4% credit" />
                            <StatCard value={stats.counties} label="Counties" />
                        </div>

                        <PipelineExplorer projects={projects} showCountyFilter={true} reportContext={`${devName}'s portfolio`} />

                        <PipelineCTA context={`a ${devName} project`} />

                        <p className="small text-muted mt-4">
                            Data source: California Tax Credit Allocation Committee (CTCAC). Map &copy; OpenStreetMap contributors.
                        </p>
                    </div>
                </section>
                <CtaSection />
            </main>
            <Footer />
            <Scrollbar />
        </Fragment>
    );
};

export default HousingDeveloperPage;
